import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, Send, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';

export default function RequestInfoDialog({ open, onOpenChange, item, onSend, mode }) {
  const effectiveRejectMode = mode === 'reject';
  const [message, setMessage] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);

  const handleGeneratePrompt = async () => {
    setGenerating(true);

    const prompt = `You are a feedback quality coach for a software product called COMPASS.
An officer submitted the following feedback that ${effectiveRejectMode ? 'is being rejected because it is not clear or actionable enough' : 'lacks sufficient detail for the development team to act on'}.

Feedback Type: ${item.type === 'bug' ? 'Bug Report' : 'Feature Request'}
Title: "${item.title}"
${item.type === 'bug' ? `Steps to Reproduce: ${item.steps_to_reproduce || 'NOT PROVIDED'}
Expected Behavior: ${item.expected_behavior || 'NOT PROVIDED'}
Actual Behavior: ${item.actual_behavior || 'NOT PROVIDED'}` : `Description: ${item.description || 'NOT PROVIDED'}`}
Link/Page: ${item.link || 'NOT PROVIDED'}
Attachments: ${item.attachments?.length ? `${item.attachments.length} file(s)` : 'None'}

Write a polite, clear, and specific message to the submitter ${effectiveRejectMode ? 'explaining why this submission is being returned and what improvements are needed before resubmission' : 'asking them to improve this submission'}. 
Be specific about EXACTLY what is missing or unclear based on what they provided vs what they left empty.
${item.type === 'bug' ? 'For bugs, emphasize the importance of steps to reproduce, expected vs actual behavior, and screenshots.' : 'For features, emphasize the need for a clear description of the desired behavior, the problem it solves, and any mockups or examples.'}
Keep it under 150 words. Start with "Hi," and end with "Thank you for helping us improve COMPASS!"
Do NOT use markdown formatting.`;

    const result = await base44.integrations.Core.InvokeLLM({ prompt });
    setMessage(result);
    setGenerating(false);
  };

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    await onSend(message.trim());
    setSending(false);
    setMessage('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="fb-requestinfodialog-dialog" className="bg-slate-900 border-slate-700 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white">{effectiveRejectMode ? 'Reject & Return Submission' : 'Request More Information'}</DialogTitle>
          <DialogDescription className="text-slate-400">
            {effectiveRejectMode
              ? `Reject and return this ${item?.type === 'bug' ? 'bug report' : 'feature request'}: "${item?.title}" — the submitter can improve and resubmit.`
              : `Ask the submitter to improve their ${item?.type === 'bug' ? 'bug report' : 'feature request'}: "${item?.title}"`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Message to Submitter</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleGeneratePrompt}
                disabled={generating}
                className="text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 h-7 text-xs"
              >
                {generating ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles className="w-3 h-3 mr-1" /> AI Draft</>
                )}
              </Button>
            </div>
            <Textarea data-testid="fb-requestinfodialog-textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe what additional information you need from the submitter..."
              className="bg-slate-800 border-slate-700 text-slate-100 min-h-[160px] text-sm"
            />
            <p className="text-[10px] text-slate-500">
              {effectiveRejectMode
                ? 'The feedback status will change to "Rejected". The submitter will be notified and can improve and resubmit.'
                : 'This message will be sent as an email and in-app notification to the submitter. The feedback status will change to "Needs Info".'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button data-testid="fb-cancel-btn" variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400">
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !message.trim()}
            className={effectiveRejectMode ? "bg-red-600 hover:bg-red-700 text-white" : "bg-amber-600 hover:bg-amber-700 text-white"}
          >
            <Send className="w-4 h-4 mr-2" />
            {sending ? 'Sending...' : effectiveRejectMode ? 'Reject & Notify Submitter' : 'Send & Set Needs Info'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}